import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import chunk from 'lodash/chunk';

import { YES_FLAG } from '../../../shared/constants';

import { decimalFormatter } from '../addEditAdjustmentModal.formatters';

import { Breakpoints } from './constants';
import periodToPeriodNumber, { monthList } from './periodToPeriodNumber';

export const calculateAnnualCummulative = ({ amount, monthsInYear, currentMonth }) =>
  amount / monthsInYear * (currentMonth + 1);

export const calculateAmounts = ({ periodNumber, recurring, annualCumulative, amount }) => {
  const monthsInYear = monthList.length;

  if (!recurring) {
    const amounts = Array(monthsInYear).fill(null);
    amounts[periodNumber] = amount;
    return amounts;
  }

  if (annualCumulative) {
    return [...Array(monthsInYear)].map((_, currentMonth) => currentMonth >= periodNumber
      ? calculateAnnualCummulative({ amount, monthsInYear, currentMonth })
      : null);
  } else {
    return [...Array(monthsInYear)].map((_, currentMonth) => currentMonth >= periodNumber
      ? amount
      : null);
  }
};

export const splitAmountsAndPeriodsIntoRows = ({ amounts, periods }) => {
  const longestValue = decimalFormatter(Math.max(...amounts)).length;

  const splits = Object.keys(Breakpoints);
  const smallestSplit = splits[splits.length - 1];

  const cellsPerRow = Object.keys(Breakpoints).reduce((acc, cellsPerRow) => {
    const maxLengthForBreakpoint = Breakpoints[cellsPerRow];
    return longestValue < maxLengthForBreakpoint
      ? cellsPerRow
      : acc;
  }, smallestSplit);

  return {
    amounts: chunk(amounts, cellsPerRow),
    periods: chunk(periods, cellsPerRow),
  };
};

const renderCells = ({ amounts, periods, periodNumber }) => amounts.map((amountsRow, rowIndex) => (
  <div className="calendar-row" key={rowIndex}>
    {amountsRow.map((amount, cellIndex) => {
      const periodName = periods[rowIndex][cellIndex];
      const totalIndex = amountsRow.length * rowIndex + cellIndex;

      const cardClassName = totalIndex < periodNumber
        ? 'calendar-card calendar-greyed-out'
        : 'calendar-card';

      return (
        <div className={cardClassName} key={cellIndex}>
          <div className="calendar-period">{periodName}</div>
          <div className="calendar-amount">{decimalFormatter(amount)}</div>
        </div>
      );
    })}
  </div>
));

class AddEditAdjustmentCalendar extends PureComponent {
  static propTypes = {
    value: PropTypes.shape({
      amount: PropTypes.string.isRequired,
      periodName: PropTypes.string.isRequired,
      frequency: PropTypes.string.isRequired,
      annualCum: PropTypes.string.isRequired,
      firstMonth: PropTypes.string.isRequired,
    }).isRequired,
    hidden: PropTypes.bool,
  };

  render() {
    const { value, hidden } = this.props;
    if (hidden) {
      return null;
    }

    const {
      periodName,
      amount,
      frequency,
      annualCum: annualCumulativeFlag,
      firstMonth,
    } = value;

    const firstMonthOffset = monthList.indexOf(firstMonth.toUpperCase());
    const offsetMonthList = [
      ...monthList.slice(firstMonthOffset, monthList.length),
      ...monthList.slice(0, firstMonthOffset),
    ];

    const recurring = frequency === YES_FLAG;
    const annualCumulative = annualCumulativeFlag === YES_FLAG;

    const periodNumber = periodToPeriodNumber(periodName, offsetMonthList);

    const amountsToSplit = calculateAmounts({ amount: Number(amount), recurring, annualCumulative, periodNumber });

    const { amounts, periods } = splitAmountsAndPeriodsIntoRows({
      amounts: amountsToSplit,
      periods: offsetMonthList,
    });

    return (
      <div className="add-edit-adjustment-calendar">
        {renderCells({ amounts, periods, periodNumber })}
      </div>
    );
  }
}

export default AddEditAdjustmentCalendar;
